/**
 * Downgrade guard for the plaintext password.
 *
 * The password never leaves this device, except for accounts the server still
 * reports as "legacy" (their stored hash is of the plaintext password). That
 * report comes from the server, so a compromised or rolled-back server — or
 * simply a tab that loaded before the account was upgraded — could ask for the
 * password of an account that no longer needs it. This device remembers every
 * account it has seen on secure sign-in and refuses to send their password
 * again, whatever the server says.
 */

import { t } from "../../shared/i18n/index.js";

export const SECURE_LOGIN_IDS_KEY = "orbital_secure_login_ids";

function defaultStorage() {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}

const normalize = (id) => String(id).trim().toLowerCase();

/** Lower-cased usernames and emails of accounts seen on secure sign-in. */
export function secureLoginIds(storage = defaultStorage()) {
  try {
    const ids = JSON.parse(storage?.getItem(SECURE_LOGIN_IDS_KEY) || "[]");
    return Array.isArray(ids) ? ids : [];
  } catch {
    return [];
  }
}

/** Remember `user` (username and email) if it signs in securely. */
export function rememberSecureLogin(user, storage = defaultStorage()) {
  if (!user || !user.auth_scheme || user.auth_scheme === "legacy") return;
  try {
    const ids = new Set(secureLoginIds(storage));
    for (const id of [user.username, user.email]) {
      if (id) ids.add(normalize(id));
    }
    storage?.setItem(SECURE_LOGIN_IDS_KEY, JSON.stringify([...ids]));
  } catch {
    /* storage unavailable — protection is best-effort */
  }
}

/**
 * Throw if any of `identifiers` (typed identifier, username, email) belongs to
 * an account this device has seen on secure sign-in.
 */
export function assertPlaintextPasswordAllowed(identifiers, storage = defaultStorage()) {
  const known = new Set(secureLoginIds(storage));
  if ((identifiers || []).some((id) => id && known.has(normalize(id)))) {
    throw new Error(t("account.error.plaintextRefused"));
  }
}

/**
 * Proof of the current password for a re-authenticated request (envelope
 * change, account deletion, identity repair, password change).
 *
 * `scheme` must be the account's scheme as the server reports it NOW (not
 * when the page loaded): the plaintext password is added only for a legacy
 * account, and only if the downgrade guard allows it.
 *
 * @param {{authKey: string, password: string, scheme: string, identifiers: string[]}} input
 *   authKey is base64
 * @returns {{auth_key: string, password?: string}}
 */
export function buildPasswordProof({ authKey, password, scheme, identifiers }, storage = defaultStorage()) {
  const proof = { auth_key: authKey };
  if (scheme === "legacy") {
    assertPlaintextPasswordAllowed(identifiers, storage);
    proof.password = password;
  }
  return proof;
}
